/**
 * <XtsStatorHeatmap> — coloured tube along a part's centerline whose
 * vertex colours map a consumer-supplied `(positionMm, value)` series
 * onto a [minColor … maxColor] gradient.
 *
 * Implementation:
 *   1. Sample the chain centerline at `partLengthMm × segmentsPerModule`
 *      mm steps → polyline of Vector3.
 *   2. Build a single TubeGeometry from a CatmullRom curve through the
 *      polyline (one draw call, no per-segment overhead).
 *   3. For each path segment, look up the heatmap value at its
 *      parametric `s`, lerp `minColor` → `maxColor` by the normalised
 *      ratio, write the colour into all radial vertices of that
 *      segment's ring.
 *   4. Render with `vertexColors: true` so the gradient flows along the
 *      tube continuously.
 *
 * Sample positions are in user-frame (passed through
 * `chainToUserPositionMm`) so the heatmap moves with the XPU's
 * `positionFrame` like every other partPosition-keyed overlay.
 */

import React, { useMemo } from 'react';
import {
  BufferAttribute,
  CatmullRomCurve3,
  Color,
  DoubleSide,
  TubeGeometry,
  Vector3,
} from 'three';
import { sampleChainRange } from '../../geometry/sampleChain.js';
import {
  interpolateHeatmapValue,
  normaliseToHeatmapRange,
  sortHeatmapSamples,
  type HeatmapSample,
} from '../../geometry/heatmapInterpolation.js';
import { chainToUserPositionMm } from '../../geometry/positionFrame.js';
import type { BuiltChain } from '../../geometry/ChainBuilder.js';
import { PartTransformGroup } from './PartTransformGroup.js';
import type { PartTransformation, PositionFrame } from '../../types.js';

const DEFAULT_MIN_COLOR = '#22c55e'; // tailwind emerald-500
const DEFAULT_MAX_COLOR = '#ef4444'; // tailwind red-500
const DEFAULT_THICKNESS_MM = 6;
const DEFAULT_DISPLACEMENT_MM = -15;
const DEFAULT_LATERAL_MM = 0;
const DEFAULT_SEGMENTS_PER_MODULE = 8;
const DEFAULT_OPACITY = 0.85;
const RADIAL_SEGMENTS = 8;

interface PartHeatmapProps {
  chain: BuiltChain;
  samples: ReadonlyArray<HeatmapSample>;
  min: number;
  max: number;
  minColor?: string;
  maxColor?: string;
  thicknessMm?: number;
  displacementMm?: number;
  lateralDisplacementMm?: number;
  segmentsPerModule?: number;
  opacity?: number;
  positionFrame?: PositionFrame;
}

const PartHeatmapImpl: React.FC<PartHeatmapProps> = ({
  chain,
  samples,
  min,
  max,
  minColor = DEFAULT_MIN_COLOR,
  maxColor = DEFAULT_MAX_COLOR,
  thicknessMm = DEFAULT_THICKNESS_MM,
  displacementMm = DEFAULT_DISPLACEMENT_MM,
  lateralDisplacementMm = DEFAULT_LATERAL_MM,
  segmentsPerModule = DEFAULT_SEGMENTS_PER_MODULE,
  opacity = DEFAULT_OPACITY,
  positionFrame,
}) => {
  // Sort once. The interpolation helper assumes monotonic input.
  const sortedSamples = useMemo(
    () => sortHeatmapSamples(samples),
    [samples],
  );

  const geometry = useMemo(() => {
    if (chain.modules.length === 0) return null;
    if (sortedSamples.length === 0) return null;

    // 1. Build a polyline along the chain centerline. stepMm is set
    // implicitly by passing 0..trackLengthMm as the range to
    // sampleChainRange with a step that yields ~`segmentsPerModule`
    // points per module.
    const meanModuleLengthMm =
      chain.trackLengthMm / Math.max(1, chain.modules.length);
    const stepMm = meanModuleLengthMm / segmentsPerModule;
    const points = sampleChainRange(
      chain,
      0,
      chain.trackLengthMm,
      stepMm,
      displacementMm,
      lateralDisplacementMm,
    );
    if (points.length < 2) return null;

    // 2. Smooth via Catmull-Rom + TubeGeometry. tubularSegments matches
    // the polyline density so each ring lands on a real path sample.
    const curve = new CatmullRomCurve3(points, false, 'catmullrom', 0);
    const tubularSegments = points.length - 1;
    const geo = new TubeGeometry(
      curve,
      tubularSegments,
      thicknessMm / 2,
      RADIAL_SEGMENTS,
      false,
    );

    // 3. Per-vertex colour. TubeGeometry vertex layout (three.js
    //    standard, stable across r150-r170):
    //      vertex(i, j) lives at index `i * (radialSegments + 1) + j`
    //    where i ∈ [0, tubularSegments] and j ∈ [0, radialSegments].
    const vertexCount = geo.attributes.position!.count;
    const colors = new Float32Array(vertexCount * 3);
    const cMin = new Color(minColor);
    const cMax = new Color(maxColor);
    const tmpColor = new Color();
    for (let i = 0; i <= tubularSegments; i++) {
      // Parametric s along the chain — uniform because we sampled the
      // polyline at uniform mm steps. CatmullRom keeps the parameter
      // close to arc-length on smooth curves; module-edge kinks may
      // drift up to a few percent, well below visible.
      const t = i / tubularSegments;
      const chainPos = t * chain.trackLengthMm;
      const userPos = chainToUserPositionMm(chainPos, positionFrame);
      const value = interpolateHeatmapValue(sortedSamples, userPos);
      const ratio =
        value !== null ? normaliseToHeatmapRange(value, min, max) : 0;
      tmpColor.copy(cMin).lerp(cMax, ratio);
      for (let j = 0; j <= RADIAL_SEGMENTS; j++) {
        const vIdx = i * (RADIAL_SEGMENTS + 1) + j;
        colors[vIdx * 3] = tmpColor.r;
        colors[vIdx * 3 + 1] = tmpColor.g;
        colors[vIdx * 3 + 2] = tmpColor.b;
      }
    }
    geo.setAttribute('color', new BufferAttribute(colors, 3));
    return geo;
  }, [
    chain,
    sortedSamples,
    min,
    max,
    minColor,
    maxColor,
    thicknessMm,
    displacementMm,
    lateralDisplacementMm,
    segmentsPerModule,
    positionFrame,
  ]);

  if (!geometry) return null;
  return (
    <mesh geometry={geometry} raycast={() => null}>
      <meshStandardMaterial
        vertexColors
        transparent={opacity < 1}
        opacity={opacity}
        side={DoubleSide}
        roughness={0.55}
        metalness={0.05}
        emissiveIntensity={0.3}
      />
    </mesh>
  );
};

const PartHeatmap = React.memo(PartHeatmapImpl);
PartHeatmap.displayName = 'XtsStatorHeatmap.Part';

interface Props {
  chainsByPartOid: Map<number, BuiltChain>;
  /** Per-part transforms keyed by `PartConfig.globalNumber`. */
  partTransformsByPartOid?: Map<number, PartTransformation | undefined>;
  parts: ReadonlyArray<{
    partOid: number;
    samples: ReadonlyArray<HeatmapSample>;
  }>;
  min: number;
  max: number;
  minColor?: string;
  maxColor?: string;
  thicknessMm?: number;
  displacementMm?: number;
  lateralDisplacementMm?: number;
  segmentsPerModule?: number;
  opacity?: number;
  positionFrame?: PositionFrame;
}

const XtsStatorHeatmapImpl: React.FC<Props> = ({
  chainsByPartOid,
  partTransformsByPartOid,
  parts,
  positionFrame,
  ...visual
}) => {
  return (
    <group name="xts-stator-heatmap">
      {parts.map((p) => {
        const chain = chainsByPartOid.get(p.partOid);
        if (!chain) return null;
        return (
          <PartTransformGroup
            key={p.partOid}
            name={`heatmap-wrap-part-${p.partOid}`}
            partTransformation={partTransformsByPartOid?.get(p.partOid)}
          >
            <PartHeatmap
              chain={chain}
              samples={p.samples}
              {...visual}
              {...(positionFrame ? { positionFrame } : {})}
            />
          </PartTransformGroup>
        );
      })}
    </group>
  );
};

export const XtsStatorHeatmap = React.memo(XtsStatorHeatmapImpl);
XtsStatorHeatmap.displayName = 'XtsStatorHeatmap';

/** Vector3 import kept only for tree-shaking transparency. */
export type { Vector3 };
